import { NGXLogger } from 'ngx-logger';
import { Component, OnInit, Input, ViewChild, Output, EventEmitter, OnDestroy, ElementRef, Inject } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { sortBy, cloneDeep, map } from 'lodash';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { Subscription } from 'rxjs';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { CustomFile } from 'src/app/message/model/custom-file';
import { SettingService } from 'src/app/core/service/setting.service';
import { DOCUMENT } from '@angular/common';
import { caretAltDownIcon } from "@progress/kendo-svg-icons";
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { Base64 } from 'js-base64';

@Component({
  selector: 'app-formly-designer',
  templateUrl: './formly-designer.component.html',
  styleUrls: ['./formly-designer.component.scss']
})
export class FormlyDesignerComponent implements OnInit, OnDestroy
{
  @ViewChild('tabstripFlag') public tabstrip: TabStripComponent;

  @Input() public assessmentId: number;
  @Input() public model = {};
  @Input() public fields: FormlyFieldConfig[];
  @Input() public fieldsPreview: FormlyFieldConfig[];
  @Input() public flags: any;
  @Input() public tasks: any;
  @Input() public serviceTypes: any;
  @Input() public channels: any;
  @Input() public needs: any;
  @Input() public isDeleted = false;
  @Input() public consentTypeAssessment = false;

  @Output() public fieldsChanged: EventEmitter<FormlyFieldConfig[]> = new EventEmitter();
  @Output() public flagsChanged: EventEmitter<any[]> = new EventEmitter();
  @Output() public tasksChanged: EventEmitter<any[]> = new EventEmitter();
  @Output() public serviceTypesChanged: EventEmitter<any[]> = new EventEmitter();
  @Output() public channelsChanged: EventEmitter<any[]> = new EventEmitter();
  @Output() public needsChanged: EventEmitter<any[]> = new EventEmitter();

  public showModel = false;
  public isNew = false;
  public isCopy = false;
  public loading = false;
  public fieldEditModel: FormlyFieldConfig;
  public usedKeys = [];
  public dragStarted = false;
  public isMouseOverActionButtons = false;
  public form = new UntypedFormGroup({});
  public communityEnabled = false;
  public icons = { caretDown: caretAltDownIcon };
  public forceResetPreview = false;
  public foundFieldInArray = false;

  public listControlTypes: Array<{ text: string; value: string; icon: string }> = [
    { text: 'Text', value: 'input', icon: 'fa-text' },
    { text: 'Text (Multiline)', value: 'textarea', icon: 'fa-text' },
    { text: 'Number', value: 'number', icon: 'fa-pen-square' },
    { text: 'Email', value: 'email', icon: 'fa-at' },
    { text: 'Select', value: 'select', icon: 'fa-caret-square-down' },
    { text: 'Radio', value: 'radio', icon: 'fa-list-alt' },
    { text: 'Checkbox', value: 'checkbox', icon: 'fa-check-square' },
    { text: 'Horizontal Line', value: 'hr', icon: 'fa-horizontal-rule' },
    { text: 'Header', value: 'header', icon: 'fa-heading' },
    { text: 'Checkbox (Multiple)', value: 'multicheckbox', icon: 'fa-ballot-check' },
    { text: 'Date', value: 'date', icon: 'fa-calendar-edit' },
    { text: 'Tab', value: 'tabs', icon: 'fa-layer-group' },
    { text: 'Label', value: 'label', icon: 'fa-code' },
    { text: 'Date Time', value: 'datetime', icon: 'fa-calendar-edit' },
    { text: 'Select (Multiple)', value: 'multiselect', icon: 'fa-caret-square-down' },
  ];

  // Import restrictions
  public fileRestrictions: FileRestrictions =
    {
      allowedExtensions: ['.json'],
      maxFileSize: 2000
    };
  public invalidFileExtensionText: string;

  private messagingTabSubscription: Subscription;

  constructor(
    private translate: TranslateService,
    private logger: NGXLogger,
    private messengerService: MessengerService,
    private messageHandlerService: MessageHandlerService,
    private elem: ElementRef,
    private settingService: SettingService,
    private assessmentService: AssessmentService,
    @Inject(DOCUMENT) private document: Document)
  {
    this.messagingTabSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type)
        {
          if (message.type === 'selectFormlyDesignerTabChannel')
          {
            this.tabstrip.selectTab(2);
          }
          else if (message.type === 'selectFormlyDesignerTabFlag')
          {
            this.tabstrip.selectTab(3);
          }
          else if (message.type === 'selectFormlyDesignerTabNeed')
          {
            this.tabstrip.selectTab(4);
          }
          else if (message.type === 'selectFormlyDesignerTabServiceType')
          {
            this.tabstrip.selectTab(5);
          }
          else if (message.type === 'selectFormlyDesignerTabTask')
          {
            this.tabstrip.selectTab(6);
          }
        }
      }
    });
  }

  public ngOnInit(): void
  {
    this.forceResetPreview = false;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.communityEnabled = sessionSetting.communityEnabled;
      }
    });

    // Get the translation used when file types is invalid
    this.translate.get(['INVALID-FILE-TYPE', 'FORM-CONTROL-TYPES']).subscribe({
      next: (res) =>
      {
        this.invalidFileExtensionText = res['INVALID-FILE-TYPE'];

        const splitted = res['FORM-CONTROL-TYPES'].split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          if (this.listControlTypes.length > i)
          {
            this.listControlTypes[i].text = splitted[i];
          }
        }
        this.listControlTypes = sortBy(this.listControlTypes, ['text']);
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingTabSubscription !== undefined) { this.messagingTabSubscription.unsubscribe(); }
  }

  /**
   * Reset form used for testing form design state.
   */
  public reset(): void
  {
    this.loading = true;
    this.forceResetPreview = false;
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.form.reset();
    this.model = {};
    this.assessmentService.setFormlyModelDefaults(this.fields, this.model);
    this.assessmentService.setFormlyControlsSum(this.fieldsPreview, this.form);
    setTimeout(() => { this.loading = false; });
  }

  /**
   * Event called when adding new control from the drop down selection.
   *
   * @param event click event
   */
  public onAddControlClick(event: any): void
  {
    if (event)
    {
      let fieldConfig: FormlyFieldConfig = null;
      // Set minimum properties
      switch (event.value)
      {
        // email, number and text are all input. templateOptions dictate which input it is.
        case 'email':
        case 'number':
        case 'input':
          fieldConfig = {
            type: 'input',
            templateOptions: {
              type: (event.value === 'input' ? 'text' : event.value)
            }
          };
          break;

        case 'radio':
        case 'select':
        case 'multiselect':
        case 'checkbox':
        case 'multicheckbox':
        case 'textarea':
        case 'date':
        case 'datetime':
          fieldConfig = {
            type: event.value
          };
          fieldConfig.templateOptions = {};
          // Init options for select and radio
          if (event.value === 'select' || event.value === 'radio' || event.value === 'multicheckbox' || event.value === 'multiselect')
          {
            fieldConfig.templateOptions.options = [];
          }
          break;

        // Tab
        case 'tabs':
          fieldConfig = {
            type: event.value,
          };
          fieldConfig.fieldGroup = [];
          break;

        // Horizontal Line
        case 'hr':
          this.isNew = true;
          this.isCopy = false;
          this.onApplyEditField({ template: '<hr />' });
          this.fieldsChanged.emit(this.fields);
          break;

        // Header
        case 'header':
          fieldConfig = {
            templateOptions: {
              type: 'header',
              label: ''
            }
          };
          break;

        // Label Text
        case 'label':
          fieldConfig = {
            type: 'label',
            templateOptions: {
              type: 'label',
              description: ''
            }
          };
          break;
      }

      if (fieldConfig)
      {
        this.isNew = true;
        this.isCopy = false;
        this.setUsedKeys();
        this.fieldEditModel = fieldConfig;
        this.fieldsChanged.emit(this.fields);
      }
    }
  }

  /**
   * Event occurs when draging is over an item is released to it's position.
   *
   * @param event Event
   */
  public onDragOver(event: any, control: any): void
  {
    event.preventDefault();
    control.moveItem(event.oldIndex, event.index);
    this.fieldsChanged.emit(this.fields);
  }

  /**
   * Event occurs when start draging an item.
   *
   * @param event Event
   */
  public onDragStart(event: any): void
  {
    if (this.isMouseOverActionButtons || this.isDeleted)
    {
      event.preventDefault();
      return;
    }
    this.dragStarted = true;
  }

  /**
   * Event occurs when drag is completed.
   */
  public onDragEnd(): void
  {
    this.dragStarted = false;
  }

  /**
   * Delete a field from the editable fields array.
   *
   * @param index Index to delete.
   */
  public deleteField(index: number, fields: any): void
  {
    // Validate if fields is being used within Flags, Tasks, Service Types or Channels rules before allowing deletion
    if (!this.canDeleteField(fields[index]))
    {
      return;
    }

    this.loading = true;
    fields.splice(index, 1);

    this.fieldsChanged.emit(this.fields);
    setTimeout(() =>
    {
      this.loading = false;
    });
  }

  /**
   * Edit a field using modal.
   *
   * @param item Edit field item to edit.
   */
  public editField(item: any): void
  {
    this.isNew = false;
    this.isCopy = false;
    this.setUsedKeys();
    this.fieldEditModel = item;
  }

  /**
   * Copy a field
   * @param item Edit field item to edit.
   */
  public copyField(item: any): void
  {
    this.isNew = false;
    this.isCopy = true;
    this.setUsedKeys();
    this.fieldEditModel = cloneDeep(item);
  }

  /**
   * Mouse enter event when mouse hover action buttons div. This is required to stop drag event.
   */
  public mouseEnterActionButtonsDiv(): void
  {
    this.isMouseOverActionButtons = true;
  }

  /**
   * Mouse leave event occurs when mouse blur the action buttons div.
   */
  public mouseLeaveActionButtonsDiv(): void
  {
    this.isMouseOverActionButtons = false;
  }

  /**
   * Field edit modal has been cancelled or closed. Being used to reset the fieldEditModel.
   *
   * @param e Event
   */
  public onCancelEditField(): void
  {
    this.fieldEditModel = undefined;
  }

  /**
   * Field edit modal has been closed and must apply changes.
   *
   * @param e Event
   */
  public onApplyEditField(field: FormlyFieldConfig | any): void
  {
    this.forceResetPreview = true;
    this.loading = true;
    this.fieldEditModel = undefined;

    if (field.key && !this.isCopy) 
    {
      this.foundFieldInArray = false;
      this.foundFieldAndUpdate(field.key.toString(), field, this.fields, null);

      if (!this.foundFieldInArray)
      {
        // If there is a tab, it must be added in the fieldGroup
        if (field.tab) 
        {
          this.foundFieldAndAdd(field.tab, field, this.fields)
        }
        // Tab have special update due to support templateOptions and fieldGroup from the same UI (where it can be a template or control)
        else if (field.type === 'tabs')
        {
          const fieldIndex = this.fields.findIndex(f => f.key === field.key);
          if (fieldIndex >= 0)
          {
            // Checking tab data to update
            field.templateOptions.options.forEach((o: any) =>
            {
              // Updating tab info in fieldGroup
              const foundField = this.fields[fieldIndex].fieldGroup.find(x => x.key === o.value);
              if (foundField)
              {
                foundField.templateOptions.label = o.label;
              }
              else
              {
                this.fields[fieldIndex].fieldGroup.push({ key: o.value, templateOptions: { label: o.label }, fieldGroup: [] });
              }

              // Updating templateOptions
              this.fields[fieldIndex].templateOptions = field.templateOptions;

              // Updating expression
              if (field.hideExpression)
              {
                this.fields[fieldIndex].hideExpression = field.hideExpression;
              }
              else
              {
                delete this.fields[fieldIndex].hideExpression;
              }

              if (field.expressionProperties)
              {
                this.fields[fieldIndex].expressionProperties = field.expressionProperties;
              }
              else
              {
                delete this.fields[fieldIndex].expressionProperties;
              }

              // Updating tab info in templateOptions
              let options: any = this.fields[fieldIndex].templateOptions.options;
              const foundOption = options.find((x: any) => x.value === o.value);
              if (foundOption)
              {
                foundOption.label = o.label;
              }
              else
              {
                options.push({ label: o.label, value: o.value, externalId: o.externalId });
              }

              // Deleting tabs if no longer needed for fieldGroup and templateOptions
              const currentTabs = map(field.fieldGroup, 'key');
              this.fields[fieldIndex].fieldGroup = this.fields[fieldIndex].fieldGroup.filter((x: any) => currentTabs.includes(x.key));

              options = this.fields[fieldIndex].templateOptions.options;
              this.fields[fieldIndex].templateOptions.options = options.filter((x: any) => currentTabs.includes(x.value));
            });

            // Re-order fieldgroup
            const orderedFieldGroup = [];
            this.fields[fieldIndex].templateOptions.options.forEach((o: any) =>
            {
              const field = this.fields[fieldIndex].fieldGroup.find((f: any) => f.key === o.value);
              if (field) { orderedFieldGroup.push(field); }
            });
            this.fields[fieldIndex].fieldGroup = orderedFieldGroup;
          }
          else
          {
            this.fields.push(field);
          }
        }
        else
        {
          this.fields.push(field);
        }
      }
    }
    else
    {
      if (this.isNew || this.isCopy)
      {
        this.fields.push(field);
      }
      else
      {
        // Locate the field to update
        const fieldIndex = this.fields.findIndex(f => f.key === field.key);
        if (fieldIndex >= 0)
        {
          this.fields[fieldIndex] = field;
        }
      }
    }

    this.logger.info('New Field', field);

    // Emit updated field changes to calling form.
    this.fieldsChanged.emit(this.fields);
    setTimeout(() =>
    {
      this.logger.info('All fields', this.fields);
      this.loading = false;
      // Make sure the field is into view after updates
      setTimeout(() =>
      {
        if (field && field.key)
        {
          const element = this.document.getElementById(field.key.toString());
          if (element)
          {
            element.scrollIntoView({ block: 'center', inline: 'nearest' });
          }
        }
      });
    });
  }

  /**
   * Flag edit form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onFlagFormChanged(flagForm: any): void
  {
    this.flagsChanged.emit(flagForm);
  }

  /**
   * Task edit form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onTaskFormChanged(taskForm: any): void
  {
    this.tasksChanged.emit(taskForm);
  }

  /**
   * Service Type edit form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onServiceTypeFormChanged(serviceTypeForm: any): void
  {
    this.serviceTypesChanged.emit(serviceTypeForm);
  }

  /**
   * Channel form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onChannelFormChanged(channelForm: any): void
  {
    this.channelsChanged.emit(channelForm);
  }

  /**
   * Need form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onNeedFormChanged(needForm: any): void
  {
    this.needsChanged.emit(needForm);
  }

  /**
   * Event occurs when tab is selected.
   *
   * @param e Event
   */
  public onTabSelect(e: SelectEvent): void
  {
    // If preview tab, must clone fields to fieldsPreview to prevent altering hideExpression
    if (e.index === 1)
    {
      this.fieldsPreview = cloneDeep(this.fields);
      setTimeout(() =>
      {
        if (this.forceResetPreview)
        {
          this.reset();
        }
        else
        {
          this.assessmentService.setFormlyModelDefaults(this.fieldsPreview, this.model);
          this.assessmentService.setFormlyControlsSum(this.fieldsPreview, this.form);
        }
      });
    }
  }

  /**
   * File has been selected.
   *
   * @param event Event
   */
  public onFileChanged(event: UploadEvent): void
  {
    event.files.forEach((file) =>
    {
      // Validate file size
      if ((file.size / 1024) > this.fileRestrictions.maxFileSize)
      {
        this.messageHandlerService.displayError('FILE-SIZE-EXCEED-LIMIT');
        return;
      }

      // Validate file extension
      let validExtension = false;
      this.fileRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });
      if (!validExtension)
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.invalidFileExtensionText.replace('{{value}}', this.fileRestrictions.allowedExtensions.join()));
        return;
      }

      const customFile = new CustomFile();
      customFile.name = file.name;
      customFile.type = file.rawFile.type;
      this.setFileFromBlob(customFile, file.rawFile);
    });
  }

  /**
   * Select attachment and open the uploader.
   */
  public selectJsonFile(): void
  {
    const uploadButton = this.elem.nativeElement.querySelectorAll('input[name="files"]');
    uploadButton[0].click();
  }

  /**
   * Key a list of used keys
   */
  private setUsedKeys(): void
  {
    this.usedKeys = [];
    this.addKeys(this.fields);
  }

  /**
   * Add used keys recursively.
   * @param fields Fields
   */
  private addKeys(fields: any): void
  {
    fields.forEach((x: any) => 
    {
      this.usedKeys.push(x.key);
      if (x.fieldGroup) 
      {
        this.addKeys(x.fieldGroup);
      }
    });
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(customFile: CustomFile, file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      const base64: any = reader.result;
      customFile.data = base64.split('base64,')[1];
      const importedData = JSON.parse(Base64.decode(customFile.data));
      if (importedData.type && importedData.fields)
      {
        this.fields = importedData.fields;
        this.fieldsChanged.emit(this.fields);
      }
      else
      {
        this.messageHandlerService.displayError('IMPORT-INVALID-ASSESSMENT-FILE');
      }
    }, false);
    reader.readAsDataURL(file);
  }

  /**
   * Validate if field can be deleted.
   *
   * @param field Field definition
   */
  private canDeleteField(field: any): boolean
  {
    let canDelete = true;

    // If formly-field as a key defined (not a separator, header, etc)
    if (field.key !== undefined)
    {
      // Validate Flags Rules
      if (this.isFieldInUseByQueryRules(this.flags, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-FLAG');
        canDelete = false;
      }

      // Validate Tasks Rules
      if (this.isFieldInUseByQueryRules(this.tasks, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-TASK');
        canDelete = false;
      }

      // Validate Service Types Rules
      if (this.isFieldInUseByQueryRules(this.serviceTypes, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-RISK');
        canDelete = false;
      }

      // Validate Channels Rules
      if (this.isFieldInUseByQueryRules(this.channels, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-CHANNEL');
        canDelete = false;
      }

      // Validate Needs Rules
      if (this.isFieldInUseByQueryRules(this.needs, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-NEED');
        canDelete = false;
      }
    }
    return canDelete;
  }

  /**
   * Check if the formly-field is used by a query-builder object.
   *
   * @param objectToValidate Object to validate.
   * @param validateKey Field name to validate.
   */
  private isFieldInUseByQueryRules(objectToValidate: any, fieldName: string): boolean
  {
    let inUse = false;

    objectToValidate.forEach(data =>
    {
      if (data.query && data.query.rules)
      {
        const obj = this.findRuleItemByKey(data.query.rules, fieldName);
        if (obj !== undefined)
        {
          inUse = true;
        }
      }
    });
    return inUse;
  }

  /**
   * Find recursively query rules that matches the fieldKey passed in.
   *
   * @param items Query object.
   * @param key Key to search.
   */
  private findRuleItemByKey(items: any, fieldKey: string): any
  {
    if (!items) { return; }
    for (const item of items)
    {
      // Test current object
      if (item.field === fieldKey) { return item; }

      // Test rules recursively
      const child = this.findRuleItemByKey(item.rules, fieldKey);
      if (child) { return child; }
    }
  }

  /**
   * Found field to update recursively
   * 
   * @param searchFieldKey Field key to search
   * @param field Field data
   * @param fields Fields data
   */
  private foundFieldAndUpdate(searchFieldKey: string, field: any, fields: any[], tabGroup: any): void
  {
    if (!this.foundFieldInArray)
    {
      let index = -1;
      fields.forEach(x =>
      {
        index += 1;
        if (x.fieldGroup)
        {
          this.foundFieldAndUpdate(searchFieldKey, field, x.fieldGroup, x);
        }
        else
        {
          // If field found. Must compare tab to see if field but be added to a new tab.
          if (x.key === searchFieldKey)
          {
            // Check if can update the field (tab selection must not have changed)
            if ((field.tab ? field.tab : null) === (tabGroup ? tabGroup.key : null))
            {
              x.templateOptions = field.templateOptions;

              if (field.hideExpression)
              {
                x.hideExpression = field.hideExpression;
              }
              else
              {
                delete x.hideExpression;
              }

              if (field.expressionProperties)
              {
                x.expressionProperties = field.expressionProperties;
              }
              else
              {
                delete x.expressionProperties;
              }

              if (field.wrappers)
              {
                x.wrappers = field.wrappers;
              }
              else
              {
                delete x.wrappers;
              }

              this.foundFieldInArray = true;
            }
            else
            {
              fields = fields.splice(index, 1);     // Delete property and will be re-added
            }
          }
        }
      });
    }
  }

  private foundFieldAndAdd(searchTabKey: string, field: any, fields: any[]): void
  {
    fields.forEach(x =>
    {
      if (x.type === 'tabs' && x.fieldGroup)
      {
        x.fieldGroup.forEach((tabGroup: any) =>
        {
          // Found the right tab
          if (tabGroup.key === searchTabKey)
          {
            // Create the array if this is the first field to add in the tab
            if (!tabGroup.fieldGroup) { tabGroup.fieldGroup = []; }
            tabGroup.fieldGroup.push(field);
          }
        });
      }
    });
  }
}
